import sharedStateReducer from "../../../redux/reducers/sharedStateReducer";
import {calculateMeleeCombatDamage} from "../../../redux/actions";
import {calcMeleeCombatInput} from "../../gameState/melee/calcMeleeCombatInput";
import {calcUnitMeleeDamageWithFortress} from "../../damage/calcUnitReceivedDamage";
import {getNeighborHexKeys} from "../../coords/getNeighborHexKeys";
import {findOwner} from "../../gameState/findOwner";

// У каждого участника боя уже есть кости: иначе расчет входа боя бросил бы
// недостающие (Math.random) прямо внутри оценки. Участник — как в
// calcMeleeCombatInput: отряд рядом с чужим, и один из двух — текущего игрока
const checkIfAllCombatantsHaveDice = (units, combatOwnerId, pendingMeleeDamage) => {
  const unitIdsWithDice = new Set(pendingMeleeDamage.map((entry) => entry.unitId));
  return units.every((unit) => {
    if (unitIdsWithDice.has(unit.id)) {
      return true;
    }
    const neighborKeys = getNeighborHexKeys(unit.hexKey);
    return !units.some((otherUnit) => neighborKeys.includes(otherUnit.hexKey)
      && otherUnit.ownerId !== unit.ownerId
      && (unit.ownerId === combatOwnerId || otherUnit.ownerId === combatOwnerId));
  });
};

/**
 * Польза исхода переброса костей отряда бота в ближнем бою (см.
 * selectBotRerollDice): бой пересчитывается настоящим редьюсером
 * CALCULATE_MELEE_COMBAT_DAMAGE с новыми гранями этого отряда — с тем же
 * распределением урона по противникам, порогами уничтожения, приоритетными
 * целями и перетеканием урона между участниками, — а пользой считается то, что
 * этот урон сделает при применении (calcUnitMeleeDamageWithFortress): потеря
 * численности чужих отрядов (у орков и в крепости она растет ступенями) и
 * повреждение крепостей, на которых они стоят, — кроме крепостей самого бота:
 * чужой отряд может занять и ее, но разрушать свое строение не польза. Урон
 * сверх уничтожения отряда бой сам не распределяет, поэтому лишний урон
 * пользы не добавляет.
 *
 * null — если просчитать бой нельзя (не у всех участников есть кости); тогда
 * решают по самой кости.
 *
 * @returns {function(number, number): number|null}
 */
export const createBotMeleeValueGetter = ({sharedState, unit, botPlayerId, combatOwnerId}) => {
  const {
    units, players, pendingMeleeDamage, specialBuildings, buildingsDamage, fortressesUnderConstruction, citiesCache,
  } = sharedState.gameState;

  if (!checkIfAllCombatantsHaveDice(units, combatOwnerId, pendingMeleeDamage)) {
    return null;
  }

  const {combatUnits, unitToEnemiesMapping} = calcMeleeCombatInput(units, combatOwnerId, pendingMeleeDamage, {}, players);
  const enemyUnits = units.filter((otherUnit) => otherUnit.ownerId !== botPlayerId);
  const ownHexKeys = new Set(enemyUnits
    .filter((enemyUnit) => findOwner(enemyUnit.hexKey, players, citiesCache || {})?.id === botPlayerId)
    .map((enemyUnit) => enemyUnit.hexKey));

  return (diceRoll1, diceRoll2) => {
    // Редьюсер сортирует входные массивы на месте — каждому исходу свой список
    const meleeCombatInput = {
      combatUnits: combatUnits.map((combatUnit) => (
        // Лишние кости селестиала убираем: в расчет идет только заданная пара
        combatUnit.id === unit.id
          ? {...combatUnit, diceRoll1, diceRoll2, diceRoll3: undefined, diceRoll4: undefined}
          : combatUnit
      )),
      unitToEnemiesMapping,
    };
    const nextPendingMeleeDamage = sharedStateReducer(
      sharedState, calculateMeleeCombatDamage(meleeCombatInput, unit.id, undefined, true)
    ).gameState.pendingMeleeDamage;

    return enemyUnits.reduce((value, enemyUnit) => {
      const {sizeLoss, fortressDamage} = calcUnitMeleeDamageWithFortress(
        enemyUnit, nextPendingMeleeDamage, specialBuildings, buildingsDamage, fortressesUnderConstruction
      );
      return value + sizeLoss + (ownHexKeys.has(enemyUnit.hexKey) ? 0 : fortressDamage);
    }, 0);
  };
};
